import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useState } from "react";
import axios from "axios";
import { backend } from "../../../../../backend";
import { useAuth } from "../../../../../context/AuthContext";
import { logAxiosError } from "../../../../../logging/error";
import { validateOptionalPhoneNumber } from "../../../../../utils/validators";
import RoundedButton from "../../../../../components/Buttons/RoundedButton/RoundedButton";
import "./userSettings.css";

const UserSettings = () => {
  const [showOrgError, setShowOrgError] = useState(false);
  const [userInfoMessage, setUserInfoMessage] = useState("");
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const auth = useAuth();

  useEffect(() => {
    if (auth.user) {
      reset({
        first_name: auth.user.first_name || "",
        last_name: auth.user.last_name || "",
        email: auth.user.email || "",
        mobile: auth.user.mobile || "",
        organization: auth.user.organization || "",
        organization_number: auth.user.organization_number || "",
      });
    }
  }, [auth.user, reset]);

  const onSubmitUserInfoHandler = async (formData) => {
    const orgError = validateOrganizationNumber(formData.organization_number);
    if (orgError) {
      setShowOrgError(orgError);
      setUserInfoMessage(false);
      return;
    }

    const updatedUserInfo = {
      ...formData,
      email: formData.email.toLowerCase(),
    };

    try {
      const { data } = await axios.post(backend("/api/users/update"), {
        token: localStorage.getItem("token"),
        userInfo: updatedUserInfo,
      });

      if (data.status) {
        localStorage.setItem("user", JSON.stringify(data.user));
        setShowOrgError(false);
        setUserInfoMessage("Dina uppgifter har sparats");
        auth.setUser(data.user);
      }
    } catch (error) {
      logAxiosError(error);
      // TODO: notify user
      if (error.response?.status === 401) {
        // invalid JWT token -> log out and return to landing page
        auth.logout();
      }
    }
  };

  const validateOrganizationNumber = (organization) => {
    if (!organization) return "Organisationsnummer är obligatoriskt.";
    if (!/^[0-9]{6}-?[0-9]{4}$/.test(organization)) {
      return "Ogiltigt organisationsnummer. Formatet ska vara 000000-0000.";
    }
    return null; // Valid
  };

  const orgNumber = watch("organization_number", "");

  const orgNumberMasking = (e) => {
    const value = e.target.value;
    const cleanValue = value.replace(/[^0-9.]+/g, "");

    if (cleanValue.length >= 7) {
      const val2 = cleanValue.slice(0, 6);
      const val3 = cleanValue.slice(6, 10);
      const formattedValue = val2 + "-" + val3;
      setValue("organization_number", formattedValue);
      return;
    }

    setValue("organization_number", cleanValue);
  };

  return (
    <>
      <form
        className="settings-form-user"
        onSubmit={handleSubmit(onSubmitUserInfoHandler)}
      >
        <h2>Mina uppgifter</h2>

        <div className="input-fields">
          <div>
            <label htmlFor="settings-firstname">Förnamn*</label>

            <input
              className={`input-field`}
              data-testid="settings-input-first-name"
              {...register("first_name", { required: true })}
              type="text"
              id="settings-firstname"
              name="first_name"
              aria-invalid={errors.first_name ? "true" : "false"}
              aria-describedby={
                errors.first_name ? "firstname-error" : undefined
              }
            />
            {errors.first_name && (
              <span
                className="settings-message settings-message--error"
                id="firstname-error"
                role="alert"
              >
                Vänligen ange ett förnamn
              </span>
            )}
          </div>

          <div>
            <label htmlFor="settings-lastname">Efternamn</label>
            <input
              className={`input-field`}
              data-testid="settings-input-last-name"
              {...register("last_name", { required: false })}
              type="text"
              id="settings-lastname"
            />
          </div>
        </div>

        <div className="input-fields">
          <div>
            <label htmlFor="settings-email">E-postadress*</label>
            <input
              className={`input-field`}
              {...register("email", {
                required: true,
                pattern: {
                  value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                  message: "Ogiltig e-postadress",
                },
              })}
              type="text"
              data-testid="settings-input-email"
              id="settings-email"
              placeholder="(enbart små bokstäver)"
              aria-invalid={errors.email ? "true" : "false"}
              aria-describedby={errors.email ? "email-error" : undefined}
            />
            {errors.email && (
              <span
                className="settings-message settings-message--error"
                id="email-error"
                role="alert"
              >
                Vänligen ange en giltig e-postadress
              </span>
            )}
          </div>

          <div>
            <label htmlFor="settings-phone">Telefonnummer</label>
            <input
              className={`input-field`}
              data-testid="settings-input-phone"
              {...register("mobile", {
                validate: validateOptionalPhoneNumber,
              })}
              aria-invalid={errors.mobile ? "true" : "false"}
              aria-describedby={errors.mobile ? "phone-error" : undefined}
              type="text"
              id="settings-phone"
            />
            {errors.mobile && (
              <span
                className="settings-message settings-message--error"
                id="phone-error"
                role="alert"
              >
                {errors.mobile.message}
              </span>
            )}
          </div>
        </div>
        <div className="input-fields">
          <div>
            <label htmlFor="settings-organisation">Organisationsnamn</label>

            <input
              className={`input-field`}
              data-testid="settings-input-organisation"
              {...register("organization", { required: false })}
              type="text"
              id="settings-organisation"
            />
          </div>

          <div>
            <label htmlFor="settings-organisation-number">
              Organisationsnummer*
            </label>

            <input
              className={`input-field`}
              data-testid="settings-input-organisation-number"
              {...register("organization_number", {
                required: true,
              })}
              onChange={orgNumberMasking}
              value={orgNumber}
              id="settings-organisation-number"
              placeholder="000000-0000"
              aria-invalid={errors.organization_number ? "true" : "false"}
              aria-describedby={
                errors.organization_number ? "org-number-error" : undefined
              }
            />
            {errors.organization_number && (
              <span
                className="settings-message settings-message--error"
                id="org-number-error"
                role="alert"
              >
                Vänligen ange ett giltigt organisationsnummer
              </span>
            )}
          </div>
        </div>
        {showOrgError && (
          <p
            className="settings-message-status settings-message--error"
            role="alert"
          >
            {showOrgError}
          </p>
        )}
        {userInfoMessage && (
          <p
            className="settings-message-status settings-message--success"
            role="alert"
          >
            {userInfoMessage}
          </p>
        )}
        {/* <button className="settings-save-btn">Spara ändringar</button> */}
        <RoundedButton color="green" label="Spara ändringar" size="md" />
      </form>
    </>
  );
};

export default UserSettings;
